import React, { useState } from "react";
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CustomCheckbox from "./CustomCheckbox";
import ProjectAvatar from "./ProjectAvatar";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditMemberRole from "../../components/Projects/EditMemberRole";
import EditPaymentDetails from "../../components/Projects/EditPaymentDetails";
import EditWeeklyLimit from "../../components/Projects/EditWeeklyLimit";
import DeleteModal from "../../components/modals/DeleteModal";

const cellStyle = {
  padding: "10px 16px",
};

const getInitials = (name) => {
  const names = name.split(" ");
  return names.map((n) => n[0]).join("").toUpperCase();
};

const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 60%, 60%)`;
};

const ManualRow = ({
  row,
  isSelected,
  onSelectToggle,
  isMembersView = false,
  isInviteView = false,
}) => {
  const [anchorEls, setAnchorEls] = useState({});

  const handleClick = (event, id) => {
    setAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleClose = (id) => {
    setAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  const renderStatusButton = () => {
    if (row.status === "Active" && !isInviteView) {
      return (
        <Button
          sx={{
            background: "#27AE60",
            color: "#fff",
            fontSize: "13px",
            textTransform: "capitalize",
          }}
        >
          Active
        </Button>
      );
    }
    if (row.status === "Pending" && !isMembersView) {
      return (
        <Button
          sx={{
            background: "#FFB82A",
            color: "#fff",
            fontSize: "13px",
            textTransform: "capitalize",
          }}
        >
          Pending
        </Button>
      );
    }
    if (row.status === "Expired" && !isMembersView) {
      return (
        <Button
          sx={{
            background: "#F4413D",
            color: "#fff",
            fontSize: "13px",
            textTransform: "capitalize",
          }}
        >
          Expired
        </Button>
      );
    }
    return null;
  };

  const [open, setOpen] = useState(false);
  const [openPaymentModal, setPaymentOpenModal] = useState(false);
  const [openWeeklyLimitModal, setWeeklyLimitOpenModal] = useState(false);
  const [openDeleteModal, setDeleteOpenModal] = useState(false);
  return (
    <>
      <TableRow
        sx={{
          "&:hover": { boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)" },
          backgroundColor: "#fff",
          borderRadius: "10px",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <TableCell
          sx={{
            ...cellStyle,
            border: "none",
            minWidth: "32px",
            maxWidth: "32px",
            borderRadius: "10px 0 0 10px",
          }}
        >
          <CustomCheckbox checked={isSelected} onChange={onSelectToggle} />
        </TableCell>

        <TableCell sx={{ ...cellStyle, border: "none" }}>{row.id}</TableCell>

        {!isMembersView && (
          <TableCell sx={{ ...cellStyle, border: "none", textAlign: "center" }}>
            {row.DayMonYear}
          </TableCell>
        )}

        <TableCell sx={{ ...cellStyle, border: "none", textAlign: "left" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              src={row.memberAvatar}
              sx={{
                width: "40px",
                height: "40px",
                fontSize: "13px",
                backgroundColor: row.memberAvatar
                  ? "transparent"
                  : stringToColor(row.memberName),
              }}
            >
              {!row.memberAvatar && getInitials(row.memberName)}
            </Avatar>
            <Box>
              <Typography fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                {row.memberName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap" }}
              >
                {row.memberRole}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        <TableCell sx={{ ...cellStyle, border: "none", textAlign: "center" }}>
          {renderStatusButton()}
        </TableCell>

        <TableCell sx={{ ...cellStyle, border: "none", textAlign: "center" }}>
          {row.role}
        </TableCell>

        <TableCell
          sx={{
            ...cellStyle,
            border: "none",
            color: "#626262",
            backgroundColor: "#fff",
            textAlign: "center",
          }}
        >
          <ProjectAvatar projects={row.projects} />
        </TableCell>

        <TableCell sx={{ ...cellStyle, border: "none", textAlign: "center" }}>
          {row.payment}
        </TableCell>

        <TableCell sx={{ ...cellStyle, border: "none", textAlign: "center" }}>
          {row.limits}
        </TableCell>

        {!isInviteView && (
          <TableCell sx={{ ...cellStyle, border: "none", textAlign: "center" }}>
            <Typography color="primary" sx={{ cursor: "pointer" }}>
              {row.timeTrackingStatus === "Enabled" ? (
                <Button
                  sx={{
                    background: "#EBF8EE",
                    color: "#38B758",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    borderRadius: "50px",
                    px: 3,
                  }}
                >
                  Enabled
                </Button>
              ) : (
                <Button
                  sx={{
                    background: "#FEECEC",
                    color: "#F4413D",
                    fontSize: "13px",
                    textTransform: "capitalize",
                    borderRadius: "50px",
                    px: 3,
                  }}
                >
                  Disabled
                </Button>
              )}
            </Typography>
          </TableCell>
        )}

        {!isInviteView && (
          <TableCell
            sx={{
              ...cellStyle,
              border: "none",
              textAlign: "center",
              borderRadius: "0px 10px 10px 0px",
            }}
          >
            <Typography color="primary" sx={{ cursor: "pointer" }}>
              {row.dateAdded}
            </Typography>
          </TableCell>
        )}

        {!isMembersView && (
          <TableCell
            sx={{
              ...cellStyle,
              border: "none",
              textAlign: "center",
              borderRadius: "0px 10px 10px 0px",
            }}
          >
            <IconButton
              onClick={(e) => handleClick(e, row.id)}
              sx={{
                border: "1px solid #0076FE",
                color: "#0076FE",
                fontSize: "14px",
                borderRadius: "30px",
                padding: "3px 16px",
                fontWeight: "500",
              }}
            >
              Action <KeyboardArrowDownIcon sx={{ marginLeft: "10px" }} />
            </IconButton>
            <Menu
              anchorEl={anchorEls[row.id]}
              open={Boolean(anchorEls[row.id])}
              onClose={() => handleClose(row.id)}
              sx={{ borderRadius: "12px" }}
            >
              {/* <MenuItem
              sx={{ fontSize: "14px", color: "#1A1F36" }}
              onClick={() => handleClose(row.id)}
            >
              Edit info 
            </MenuItem> */}
              <MenuItem
                sx={{ fontSize: "14px", color: "#1A1F36" }}
                // onClick={() => handleClose(row.id)}
                onClick={() => setOpen(true)}
              >
                Edit role and memberships
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "14px", color: "#1A1F36" }}
                // onClick={() => handleClose(row.id)}
                onClick={() => setPaymentOpenModal(true)}
              >
                Edit payment details
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "14px", color: "#1A1F36" }}
                // onClick={() => handleClose(row.id)}
                onClick={() => setWeeklyLimitOpenModal(true)}
              >
                Edit Weekly limits
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "14px", color: "#1A1F36" }}
                onClick={() => handleClose(row.id)}
              >
                Resend Invite
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "14px", color: "#1A1F36" }}
             onClick={() => setDeleteOpenModal(true)}
              >
                Delete Invite
              </MenuItem>
              {/* <MenuItem
                sx={{ fontSize: "14px", color: "#1A1F36" }}
                onClick={() => handleClose(row.id)}
              >
                Remove member
              </MenuItem> */}
            </Menu>
          </TableCell>
        )}
      </TableRow>

      <EditMemberRole
        open={open}
        onClose={() => setOpen(false)}
      />
      <EditPaymentDetails
        open={openPaymentModal}
        onClose={() => setPaymentOpenModal(false)}
      />

      <EditWeeklyLimit
        open={openWeeklyLimitModal}
        onClose={() => setWeeklyLimitOpenModal(false)}
      />

      <DeleteModal
        open={openDeleteModal}
        onClose={() => setDeleteOpenModal(false)}
      />
    </>

  );
};

export default ManualRow;
